/**
 * WFM (Workflow Manager) Client
 * Handles communication with the Workflow Manager API for agent CRUD operations
 */

import { getToken } from './auth';
import { getIdentity, getWorkflowManagerUrl } from './config/services';
import type {
  WFMAgent,
  AgentConfig,
  CreateAgentResponse,
  MCPServerBinding,
  SelectedToolConfig,
  SubAgentBinding,
} from '../types/agent-builder';

// =============================================================================
// AUTH HELPERS
// =============================================================================

/**
 * Get authentication headers for WFM requests
 */
async function getAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const token = await getToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  try {
    const identity = getIdentity();
    if (identity.tenantId) {
      headers['X-Tenant-Id'] = identity.tenantId;
      headers['tenant'] = identity.tenantId;
    }
    if (identity.userId) {
      headers['X-User-Id'] = identity.userId;
    }
  } catch {
    // Identity not set, continue without headers
  }

  return headers;
}

// =============================================================================
// DRAFT WORKFLOW OPERATIONS
// =============================================================================

import type { WFMWorkflow } from '../types/agent-builder';

/**
 * Create a new draft workflow
 * POST /workflow/{project_id}/{email}
 *
 * @param projectId - Project ID
 * @param email - User email
 * @param config - Agent configuration
 * @returns Created workflow with workflow_id
 */
export async function createDraft(
  projectId: string,
  email: string,
  config: AgentConfig
): Promise<{ workflow_id: string }> {
  const baseUrl = getWorkflowManagerUrl();
  const url = `${baseUrl}/workflow/${projectId}/${encodeURIComponent(email)}`;

  const headers = await getAuthHeaders();
  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      name: config.name,
      description: config.description,
      workflow_type: 'flows_alpha_agent',
      workflow_variant: config.sdk,
      meta_data: configToMetaData(config),
    }),
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(error.detail || `Failed to create draft: ${res.statusText}`);
  }

  return res.json();
}

/**
 * Update an existing draft workflow
 * PUT /workflow/{project_id}/{workflow_id}/{email}
 *
 * @param projectId - Project ID
 * @param workflowId - Workflow ID
 * @param email - User email
 * @param config - Updated agent configuration
 */
export async function updateDraft(
  projectId: string,
  workflowId: string,
  email: string,
  config: AgentConfig
): Promise<void> {
  const baseUrl = getWorkflowManagerUrl();
  const url = `${baseUrl}/workflow/${projectId}/${workflowId}/${encodeURIComponent(email)}`;

  const headers = await getAuthHeaders();
  const res = await fetch(url, {
    method: 'PUT',
    headers,
    body: JSON.stringify({
      name: config.name,
      description: config.description,
      meta_data: configToMetaData(config),
    }),
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(error.detail || `Failed to update draft: ${res.statusText}`);
  }
}

/**
 * Load an existing draft workflow
 * GET /workflow/current/{email}/{project_id}/{workflow_id}
 *
 * @param projectId - Project ID
 * @param workflowId - Workflow ID
 * @param email - User email
 * @returns Workflow with config
 */
export async function loadDraft(
  projectId: string,
  workflowId: string,
  email: string
): Promise<WFMWorkflow> {
  const baseUrl = getWorkflowManagerUrl();
  const url = `${baseUrl}/workflow/current/${encodeURIComponent(email)}/${projectId}/${workflowId}`;

  const headers = await getAuthHeaders();
  const res = await fetch(url, { headers });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(error.detail || `Failed to load draft: ${res.statusText}`);
  }

  return res.json();
}

/**
 * List all workflows for a user in a project
 * GET /workflow/{project_id}/{email}
 *
 * @param projectId - Project ID
 * @param email - User email
 * @returns Array of workflows (drafts and published)
 */
export async function listWorkflows(
  projectId: string,
  email: string
): Promise<WFMWorkflow[]> {
  const baseUrl = getWorkflowManagerUrl();
  const url = `${baseUrl}/workflow/${projectId}/${encodeURIComponent(email)}`;

  const headers = await getAuthHeaders();
  const res = await fetch(url, { headers });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(error.detail || `Failed to list workflows: ${res.statusText}`);
  }

  const data = await res.json();
  // Handle different response formats
  if (Array.isArray(data)) {
    return data;
  }
  if (data.workflows) {
    return data.workflows;
  }
  if (data.data) {
    return data.data;
  }
  return [];
}

/**
 * Delete a draft workflow
 * DELETE /workflow/{project_id}/{workflow_id}/{email}
 *
 * @param projectId - Project ID
 * @param workflowId - Workflow ID
 * @param email - User email
 */
export async function deleteDraft(
  projectId: string,
  workflowId: string,
  email: string
): Promise<void> {
  const baseUrl = getWorkflowManagerUrl();
  const url = `${baseUrl}/workflow/${projectId}/${workflowId}/${encodeURIComponent(email)}`;

  const headers = await getAuthHeaders();
  const res = await fetch(url, {
    method: 'DELETE',
    headers,
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(error.detail || `Failed to delete draft: ${res.statusText}`);
  }
}

/**
 * Convert AgentConfig to meta_data format for WFM storage
 */
function configToMetaData(config: AgentConfig): Record<string, unknown> {
  return {
    sdk: config.sdk,
    llm: config.llm,
    instructions: config.instructions,
    mcp_servers: config.mcp_servers,
    sub_agents: config.sub_agents,
    tools: config.tools,
    input_schema: config.input_schema,
    output_schema: config.output_schema,
    capabilities: config.capabilities,
    usage_examples: config.usage_examples,
    venv_blob_url: config.venv_blob_url,
    entry_point: config.entry_point,
    base_path: config.base_path,
    agent_file: config.agent_file,
  };
}

/**
 * Convert WFM workflow meta_data to AgentConfig
 */
export function workflowToConfig(workflow: WFMWorkflow): AgentConfig {
  const meta = workflow.meta_data || {};
  return {
    name: workflow.name,
    description: workflow.description || '',
    version: workflow.version || (meta.version as string) || '1.0.0',
    sdk: (meta.sdk as AgentConfig['sdk']) || 'eureka_agent',
    llm: (meta.llm as AgentConfig['llm']) || {
      model: 'claude-sonnet-4-20250514',
      provider: 'anthropic',
    },
    instructions: (meta.instructions as string) || '',
    tools: meta.tools as string[] | undefined,
    // mcp_servers_full has full binding objects (with res_id); mcp_servers has slim format
    mcp_servers: (meta.mcp_servers_full || meta.mcp_servers) as AgentConfig['mcp_servers'],
    sub_agents: meta.sub_agents as AgentConfig['sub_agents'],
    input_schema: meta.input_schema as unknown[],
    output_schema: meta.output_schema as unknown[],
    capabilities: meta.capabilities as string[],
    usage_examples: meta.usage_examples as string[],
    venv_blob_url: meta.venv_blob_url as string,
    entry_point: meta.entry_point as string,
    base_path: meta.base_path as string,
    agent_file: meta.agent_file as string,
  };
}

// =============================================================================
// AGENT LISTING
// =============================================================================

/**
 * List agents from WFM
 * GET /agents
 *
 * @param params - Query parameters for filtering
 * @returns Array of agents
 */
export async function listAgents(params?: {
  project_id?: string;
  tenant_id?: string;
  category?: string;
  status?: string;
  page_size?: number;
  page_number?: number;
}): Promise<WFMAgent[]> {
  const baseUrl = getWorkflowManagerUrl();

  // Build query string manually to support relative URLs
  const queryParams: string[] = [];
  if (params?.project_id) queryParams.push(`project_id=${encodeURIComponent(params.project_id)}`);
  if (params?.tenant_id) queryParams.push(`tenant_id=${encodeURIComponent(params.tenant_id)}`);
  if (params?.category) queryParams.push(`category=${encodeURIComponent(params.category)}`);
  if (params?.status) queryParams.push(`status=${encodeURIComponent(params.status)}`);
  if (params?.page_size) queryParams.push(`page_size=${params.page_size}`);
  if (params?.page_number) queryParams.push(`page_number=${params.page_number}`);

  const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
  const url = `${baseUrl}/agents${queryString}`;

  const headers = await getAuthHeaders();
  const res = await fetch(url, { headers });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(error.detail || `Failed to list agents: ${res.statusText}`);
  }

  const data = await res.json();

  // Handle different response formats
  if (Array.isArray(data)) {
    return data;
  }
  if (data.agents) {
    return data.agents;
  }
  if (data.data) {
    return data.data;
  }

  return [];
}

/**
 * Get a single agent by ID
 * GET /agents/{agent_id}
 *
 * @param agentId - The agent ID
 * @returns Agent details
 */
export async function getAgent(agentId: string): Promise<WFMAgent | null> {
  const baseUrl = getWorkflowManagerUrl();
  const headers = await getAuthHeaders();

  const res = await fetch(`${baseUrl}/agents/${agentId}`, { headers });

  if (!res.ok) {
    if (res.status === 404) {
      return null;
    }
    const error = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(error.detail || `Failed to get agent: ${res.statusText}`);
  }

  return res.json();
}

// =============================================================================
// PROJECT LISTING
// =============================================================================

/**
 * Project from WFM
 */
export interface WFMProject {
  project_id: string;
  name: string;
  description?: string;
  email?: string;
  created_at?: string;
  modified_at?: string;
}

/**
 * List user's projects from WFM
 * GET /projects/user-project?email=...
 *
 * @param email - User email
 * @param params - Optional pagination params
 * @returns Array of projects
 */
export async function listProjects(
  email: string,
  params?: {
    name?: string;
    page_size?: number;
    page_number?: number;
  }
): Promise<WFMProject[]> {
  const baseUrl = getWorkflowManagerUrl();

  const queryParams: string[] = [`email=${encodeURIComponent(email)}`];
  if (params?.name) queryParams.push(`name=${encodeURIComponent(params.name)}`);
  if (params?.page_size) queryParams.push(`page_size=${params.page_size}`);
  if (params?.page_number) queryParams.push(`page_number=${params.page_number}`);

  const url = `${baseUrl}/projects/user-project?${queryParams.join('&')}`;

  const headers = await getAuthHeaders();
  const res = await fetch(url, { headers });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(error.detail || error.message || `Failed to list projects: ${res.statusText}`);
  }

  const data = await res.json();

  // Handle response format: { data: { projects: [...], ... } }
  if (data.data?.projects) {
    return data.data.projects;
  }
  if (data.data && Array.isArray(data.data)) {
    return data.data;
  }
  if (data.projects) {
    return data.projects;
  }

  return [];
}

// =============================================================================
// TOOL GENERATION HELPERS
// =============================================================================

/**
 * Agent tool configuration format for agent.yaml
 * This matches the structure expected by the agent executor
 */
interface AgentToolConfig {
  name: string;
  description: string;
  async?: boolean;
  timeout?: number;
  requires_permission?: {
    message: string;
  };
  agent?: string;  // For subagent tools: UUID or agent type path (e.g., "system/creator/summarizer")
}

/**
 * Generate tool name in agent.yaml format
 * Format: mcp__<server_name>__<tool_name>
 */
function generateMCPToolName(serverName: string, toolName: string): string {
  // Sanitize server name (replace spaces, hyphens with underscores, lowercase)
  const sanitizedServer = serverName.replace(/[-\s]/g, '_').toLowerCase();
  return `mcp__${sanitizedServer}__${toolName}`;
}

/**
 * Generate tools array from MCP server bindings with their selected tools
 * Also generates agent-tools from sub_agents configuration
 *
 * If selected_tools is undefined, all tools from that server are included
 * If selected_tools is an array, only those tools are included
 *
 * Subagents are declared as tools with "agent:" field - the executor
 * processes these as agent-tools that invoke subagent calls.
 *
 * @param mcpServers - MCP server bindings with optional selected_tools
 * @param subAgents - Sub-agent bindings to expose as tools
 * @returns Array of tool configurations for agent.yaml
 */
function generateToolsFromConfig(
  mcpServers?: MCPServerBinding[],
  subAgents?: SubAgentBinding[]
): AgentToolConfig[] {
  const tools: AgentToolConfig[] = [];

  // Add MCP tools
  if (mcpServers && mcpServers.length > 0) {
    for (const server of mcpServers) {
      if (server.selected_tools && server.selected_tools.length > 0) {
        // Use the explicitly selected tools
        for (const tool of server.selected_tools) {
          tools.push({
            name: tool.name, // Already in mcp__server__tool format
            description: tool.description,
            async: tool.async,
            timeout: tool.timeout,
            requires_permission: tool.requires_permission,
          });
        }
      }
      // If selected_tools is undefined, the server is included but tools will be
      // dynamically loaded at runtime (all tools enabled)
      // We don't add individual tools here - the agent executor will load them
    }
  }

  // Add subagent tools (tools with "agent:" field)
  // The executor recognizes these as agent-tools and routes through run_agent internally
  // async/timeout/description are implicit - loaded from target agent's definition
  if (subAgents && subAgents.length > 0) {
    for (const subagent of subAgents) {
      // Generate tool name from subagent name (snake_case)
      const toolName = subagent.name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
      tools.push({
        name: toolName,
        description: '', // Will be loaded from target agent's description
        agent: subagent.agent_id, // UUID or agent type path - signals this is an agent-tool
      });
    }
  }

  // Always add the ask_user tool for user interaction
  tools.push({
    name: 'ask_user',
    description: 'Ask the user for clarification or confirmation',
    async: true,
    timeout: 300,
  });

  return tools;
}

/**
 * Generate mcp_servers array for agent.yaml
 *
 * agent.yaml declares what the agent NEEDS (no actual resource IDs):
 *   mcp_servers:
 *     - name: <handle>                # user-defined unique name (lookup key in bindings)
 *       type_template: <template>     # functional template name (what kind of MCP server)
 *       required: true
 *
 * The actual binding (handle -> res_id) goes in bindings.resource_bindings
 */
function generateMCPServersForAgentYaml(mcpServers?: MCPServerBinding[]): Array<{
  name: string;
  type_template?: string;
  required: boolean;
}> {
  if (!mcpServers) return [];

  return mcpServers.map(server => ({
    name: server.handle,
    type_template: server.type_template,
    required: true,
  }));
}

/**
 * Generate resource_bindings for runtime
 *
 * runtime_bindings provides actual bindings per environment:
 *   resource_bindings:
 *     <handle>: <res_id>           # MCP server: handle matches name in agent.yaml mcp_servers
 *     <tool_name>: <deployment_id> # Sub-agent: tool_name matches name in agent.yaml tools[].name
 */
function generateResourceBindings(mcpServers?: MCPServerBinding[], subAgents?: SubAgentBinding[]): Record<string, string> {
  const bindings: Record<string, string> = {};

  // MCP server bindings: handle -> res_id
  if (mcpServers) {
    for (const server of mcpServers) {
      if (server.res_id) {
        bindings[server.handle] = server.res_id;
      }
    }
  }

  // Sub-agent bindings: tool_name -> deployment_id
  if (subAgents) {
    for (const subagent of subAgents) {
      if (subagent.deployment_id) {
        const toolName = subagent.name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
        bindings[toolName] = subagent.deployment_id;
      }
    }
  }

  return bindings;
}

// =============================================================================
// AGENT CREATION
// =============================================================================

/**
 * Create a FlowsAlpha agent
 * POST /workflow/{project_id}/{email}/flows-alpha-agent
 *
 * @param projectId - Project ID
 * @param email - User email (for ownership)
 * @param config - Agent configuration
 * @returns Created agent response
 */
export async function createFlowsAlphaAgent(
  projectId: string,
  email: string,
  config: AgentConfig
): Promise<CreateAgentResponse> {
  const baseUrl = getWorkflowManagerUrl();
  const url = `${baseUrl}/workflow/${projectId}/${encodeURIComponent(email)}/flows-alpha-agent`;

  // Generate tools array from MCP servers AND sub-agents
  // Sub-agents are exposed as tools with "agent:" field - executor recognizes these as agent-tools
  const tools = generateToolsFromConfig(config.mcp_servers, config.sub_agents);

  // Generate mcp_servers for agent.yaml (declares what agent NEEDS, no res_ids)
  const mcpServersForYaml = generateMCPServersForAgentYaml(config.mcp_servers);

  const headers = await getAuthHeaders();

  // Build agent_yaml object - this is what the executor reads
  // WFM loader checks: if sdk == "eureka_agent" and meta_data.get("agent_yaml")
  // agent.yaml declares WHAT the agent needs (no actual bindings)
  const agentYaml: Record<string, unknown> = {
    name: config.name,
    sdk: config.sdk || 'eureka_agent',
    llm: config.llm,
    tools: tools.length > 0 ? tools : undefined,
    mcp_servers: mcpServersForYaml.length > 0 ? mcpServersForYaml : undefined,
    input_schema: config.input_schema,
    output_schema: config.output_schema,
  };

  // Use the version from config (user-managed, defaults to '1.0.0').
  // VS Code extension uses git commit hashes; web UI uses semantic versions.
  // WFM enforces uniqueness on (name, project_id, version).
  const version = config.version || '1.0.0';

  // Build slim mcp_servers for meta_data (name + type only, no res_id)
  const mcpServersMeta = (config.mcp_servers || []).map((s) => ({
    name: s.handle,
    type: s.type || 'mcp',
    timeout: s.timeout,
  }));

  const requestBody = {
    name: config.name,
    description: config.description,
    version,
    workflow_type: 'flows_alpha_agent',
    workflow_variant: config.sdk || 'eureka_agent',
    resources: [],
    meta_data: {
      // Required fields (matching VS Code extension's FlowsAlphaAgentRequest)
      name: config.name,
      sdk: config.sdk || 'eureka_agent',
      input_schema: config.input_schema || [],
      output_schema: config.output_schema || [],
      // Skill agent fields
      agent_yaml: agentYaml,
      prompt_md: config.instructions,
      // LLM config
      llm: config.llm,
      // Tools and MCP servers (slim format, no res_ids)
      tools: tools.length > 0 ? tools : undefined,
      mcp_servers: mcpServersMeta.length > 0 ? mcpServersMeta : undefined,
      // Display/UI fields
      capabilities: config.capabilities,
      usage_examples: config.usage_examples,
      // Full binding objects for UI to reload (not consumed by executor)
      instructions: config.instructions,
      sub_agents: config.sub_agents,
      mcp_servers_full: config.mcp_servers,
      // ProCode fields (optional)
      venv_blob_url: config.venv_blob_url,
      entry_point: config.entry_point,
      base_path: config.base_path,
      agent_file: config.agent_file,
    },
    // Resource bindings are NOT in meta_data — they belong in deployment_fields
  };

  console.log('[wfmClient] Creating FlowsAlpha agent:', {
    url,
    name: config.name,
    version,
    sdk: config.sdk,
    toolCount: tools.length,
    mcpServerCount: mcpServersForYaml.length,
  });

  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(requestBody),
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ message: res.statusText }));
    const msg = error.message || error.detail || `Failed to create agent: ${res.statusText}`;
    // Surface a user-friendly message for version conflicts
    if (res.status === 400 && typeof msg === 'string' && msg.includes('already published')) {
      throw new Error(`Version '${version}' is already published. Please bump the version number and try again.`);
    }
    throw new Error(msg);
  }

  const data = await res.json();
  // WFM wraps response in { status_code, data, message }
  return data.data || data;
}

// =============================================================================
// AGENT DEPLOYMENT
// =============================================================================

/**
 * Deployment response from WFM
 */
export interface DeploymentResponse {
  deployment_id: string;
  name: string;
  agent_id: string;
  user_id: string;
  deployment_fields: Record<string, unknown>;
  [key: string]: unknown;
}

/**
 * Create an on-demand deployment for an agent.
 * POST /agent_deployment/deploy/ondemand
 *
 * A deployment is required to run an agent. The deployment:
 * - Creates a concrete runnable instance of the agent
 * - Auto-creates a run_agent MCP resource (enabling sub-agent consumption)
 * - Returns a deployment_id used in POST /start
 *
 * @param agentId - Published agent ID
 * @param name - Deployment name (must be unique per agent+user)
 * @param userId - User creating the deployment
 * @param resourceBindings - MCP handle → resource UUID, sub-agent tool → deployment UUID
 * @param toolConfig - Tool configuration values (SIMPLE and SECRET fields)
 * @returns Deployment response with deployment_id
 */
export async function createDeployment(
  agentId: string,
  name: string,
  userId: string,
  resourceBindings: Record<string, string> = {},
  toolConfig: Record<string, string> = {},
): Promise<DeploymentResponse> {
  const baseUrl = getWorkflowManagerUrl();
  const url = `${baseUrl}/agent_deployment/deploy/ondemand`;

  const headers = await getAuthHeaders();

  const requestBody = {
    name,
    agent_id: agentId,
    user_id: userId,
    deployment_fields: {
      resource_bindings: resourceBindings,
      ...(Object.keys(toolConfig).length > 0 ? { tool_config: toolConfig } : {}),
    },
    execute_now: false,  // Only create deployment, don't execute
  };

  console.log('[wfmClient] Creating deployment:', {
    url,
    agentId,
    name,
    userId,
    resourceBindings: Object.keys(resourceBindings),
  });

  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(requestBody),
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ message: res.statusText }));
    throw new Error(error.message || error.detail || `Failed to create deployment: ${res.statusText}`);
  }

  const data = await res.json();
  return data.data || data;
}

/**
 * Get deployments for an agent.
 * GET /agent_deployment?agent_id=...
 *
 * @param agentId - Agent ID to get deployments for
 * @returns Array of deployments
 */
export async function getDeployments(agentId: string): Promise<DeploymentResponse[]> {
  const baseUrl = getWorkflowManagerUrl();
  const url = `${baseUrl}/agent_deployment?agent_id=${encodeURIComponent(agentId)}`;

  const headers = await getAuthHeaders();
  const res = await fetch(url, { headers });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(error.detail || `Failed to get deployments: ${res.statusText}`);
  }

  const data = await res.json();

  // Handle WFM response format: { status_code: 200, data: [...] }
  if (data.data && Array.isArray(data.data)) {
    return data.data;
  }
  if (Array.isArray(data)) {
    return data;
  }
  return [];
}

// =============================================================================
// AGENT UPDATES
// =============================================================================

/**
 * Update an existing agent
 * PATCH /agents/{agent_id}
 *
 * @param agentId - Agent ID to update
 * @param updates - Partial agent config to update
 * @returns Updated agent
 */
export async function updateAgent(
  agentId: string,
  updates: Partial<AgentConfig>
): Promise<WFMAgent> {
  const baseUrl = getWorkflowManagerUrl();
  const headers = await getAuthHeaders();

  const res = await fetch(`${baseUrl}/agents/${agentId}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify({
      name: updates.name,
      description: updates.description,
      version: updates.version,
      meta_data: updates,
    }),
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(error.detail || `Failed to update agent: ${res.statusText}`);
  }

  return res.json();
}

/**
 * Create a new version of an existing agent
 * POST /workflow/{project_id}/{email}/flows-alpha-agent with same name
 *
 * @param projectId - Project ID
 * @param email - User email
 * @param config - New agent config with incremented version
 * @returns New version response
 */
export async function createAgentVersion(
  projectId: string,
  email: string,
  config: AgentConfig
): Promise<CreateAgentResponse> {
  // Creating with same name but new version creates a new version
  return createFlowsAlphaAgent(projectId, email, config);
}

// =============================================================================
// AGENT DELETION
// =============================================================================

/**
 * Delete an agent
 * DELETE /agents/{agent_id}
 *
 * @param agentId - Agent ID to delete
 */
export async function deleteAgent(agentId: string): Promise<void> {
  const baseUrl = getWorkflowManagerUrl();
  const headers = await getAuthHeaders();

  const res = await fetch(`${baseUrl}/agents/${agentId}`, {
    method: 'DELETE',
    headers,
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(error.detail || `Failed to delete agent: ${res.statusText}`);
  }
}

// =============================================================================
// AGENT SEARCH
// =============================================================================

/**
 * Search agents by name or description
 *
 * @param query - Search query
 * @param params - Additional filter params
 * @returns Matching agents
 */
export async function searchAgents(
  query: string,
  params?: {
    project_id?: string;
    tenant_id?: string;
    category?: string;
  }
): Promise<WFMAgent[]> {
  // For now, use list and filter client-side
  // TODO: Implement server-side search when available
  const agents = await listAgents({
    project_id: params?.project_id,
    tenant_id: params?.tenant_id,
    category: params?.category,
  });

  const lowerQuery = query.toLowerCase();
  return agents.filter(
    (agent) =>
      agent.name.toLowerCase().includes(lowerQuery) ||
      agent.description?.toLowerCase().includes(lowerQuery)
  );
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Convert WFMAgent meta_data to AgentConfig
 */
export function wfmAgentToConfig(agent: WFMAgent): AgentConfig {
  const meta = agent.meta_data || {};
  return {
    name: agent.name,
    description: agent.description || '',
    version: agent.version || (meta.version as string) || '1.0.0',
    sdk: (meta.sdk as AgentConfig['sdk']) || 'openai_agents',
    llm: (meta.llm as AgentConfig['llm']) || {
      model: 'gpt-4o',
      provider: 'azure_openai',
    },
    instructions: (meta.instructions as string) || '',
    tools: meta.tools as string[] | undefined,
    mcp_servers: (meta.mcp_servers_full || meta.mcp_servers) as AgentConfig['mcp_servers'],
    sub_agents: meta.sub_agents as AgentConfig['sub_agents'],
    input_schema: meta.input_schema as unknown[],
    output_schema: meta.output_schema as unknown[],
    capabilities: meta.capabilities as string[],
    usage_examples: meta.usage_examples as string[],
    venv_blob_url: meta.venv_blob_url as string,
    entry_point: meta.entry_point as string,
    base_path: meta.base_path as string,
    agent_file: meta.agent_file as string,
  };
}

/**
 * Check if an agent name is available
 *
 * @param name - Agent name to check
 * @param projectId - Project ID
 * @returns True if name is available
 */
export async function isAgentNameAvailable(
  name: string,
  projectId?: string
): Promise<boolean> {
  try {
    const agents = await listAgents({ project_id: projectId });
    return !agents.some((a) => a.name.toLowerCase() === name.toLowerCase());
  } catch {
    // If listing fails, assume name is available
    return true;
  }
}

// =============================================================================
// LLM RESOURCES
// =============================================================================

/**
 * LLM Resource from WFM data_connection endpoint
 */
export interface LLMResource {
  id: string;
  name: string;
  display_name?: string;
  type: string;
  provider?: string;
}

/**
 * List available LLM resources from WFM
 *
 * Calls GET /data_connection and filters for resources.llm.* types.
 * The data_connection endpoint returns all resources; we filter client-side
 * for types starting with "resources.llm.".
 *
 * @returns Array of LLM resources
 */
export async function listLLMResources(): Promise<LLMResource[]> {
  const baseUrl = getWorkflowManagerUrl();
  const headers = await getAuthHeaders();

  const res = await fetch(`${baseUrl}/data_connection`, { headers });

  if (!res.ok) {
    console.error(`[wfmClient] Failed to fetch data_connection: ${res.status} ${res.statusText}`);
    return [];
  }

  const data = await res.json();

  // Handle response formats: { data: { resources: [...] } } or { data: [...] } or { resources: [...] }
  let resourceList: Array<Record<string, unknown>>;
  if (data.data && typeof data.data === 'object' && 'resources' in data.data) {
    resourceList = (data.data.resources || []) as Array<Record<string, unknown>>;
  } else if (Array.isArray(data.data)) {
    resourceList = data.data as Array<Record<string, unknown>>;
  } else if (data.resources) {
    resourceList = data.resources as Array<Record<string, unknown>>;
  } else {
    resourceList = [];
  }

  // Filter for LLM resources (type starts with "resources.llm.")
  return resourceList
    .filter((r) => {
      const dsl = r.dsl as { resource_type?: string } | undefined;
      const type = dsl?.resource_type || (r.type as string) || '';
      return type.startsWith('resources.llm.');
    })
    .map((r) => {
      const dsl = r.dsl as { resource_type?: string } | undefined;
      const type = dsl?.resource_type || (r.type as string) || '';
      // Extract provider from type: "resources.llm.AzureOpenAIV2" -> "AzureOpenAIV2"
      const provider = type.replace('resources.llm.', '');
      return {
        id: r.id as string,
        name: r.name as string,
        display_name: (r.display_name as string) || undefined,
        type,
        provider,
      };
    });
}

// =============================================================================
// EXPORTS
// =============================================================================

export const wfmClient = {
  // Draft operations
  createDraft,
  updateDraft,
  loadDraft,
  listWorkflows,
  deleteDraft,
  workflowToConfig,

  // Agent listing
  listAgents,
  getAgent,
  searchAgents,

  // Agent creation
  createFlowsAlphaAgent,
  createAgentVersion,

  // Agent deployment
  createDeployment,
  getDeployments,

  // Agent updates
  updateAgent,

  // Agent deletion
  deleteAgent,

  // LLM Resources
  listLLMResources,

  // Utilities
  wfmAgentToConfig,
  isAgentNameAvailable,
};
